import { Loader } from "@allwyn/design-system/src/loader/loader";
import { showAlert } from "@allwyn/design-system/src/modal-alert/show-alert";
import { triggerMobileAppEvent } from "@allwyn/mobile-apps/mobile-app-integration";
import { useSessionStorage } from "@allwyn/storage/composables/use-storage";
import { logDebugEvent, logException } from "@allwyn/utils/logger/use-app-logger";
import { fixedHelpCobrowsingMode } from "../fixed-help";

/* eslint-disable @typescript-eslint/naming-convention */
export interface ChatPlayerData {
    Username: string;
    PlayerID: string;
    FirstName: string;
    Language: string;
    Balance: string;
    Currency: string;
    VipLevel: string;
    Depositor: string;
    SessionKey?: string;
}

let chatLoaded = false;
let storage: ReturnType<typeof useSessionStorage>;
let sessionWatcherCallback: ((sce_instance: any) => void) | null = null;

export const setChatSessionWatcher = (callback: (sce_instance: any) => void) => {
    sessionWatcherCallback = callback;
};

const loadChatProvider = () => {
    const promise = new Promise<void>((res, rej) => {
        if (chatLoaded) {
            res();
            return;
        }
        const scriptElement = document.createElement("script");
        scriptElement.type = "text/javascript";
        scriptElement.async = true;

        scriptElement.addEventListener("error", () => rej(new Error("Nepodařilo se načíst script chatu")), {
            passive: true,
        });

        const referrer = document.referrer
            ? encodeURIComponent(document.referrer.substring(document.referrer.indexOf("://") + 1))
            : "";
        const location = window.location
            ? encodeURIComponent(window.location.href.substring(window.location.protocol.length))
            : "";

        const chatPath =
            "/index.php/cse/chat/getstatus/(click)/internal/(position)/api/(ma)/br/(top)/350/(units)/pixels/(leaveamessage)/true/(identifier)/prihlaseni/(survey)/1";
        scriptElement.src = `${appSettings["Integrations:Chat:BaseAddress"]}${chatPath}?r="${referrer}&l=${location}`;
        document.body.appendChild(scriptElement);

        // periodically check, if chat is loaded
        const intervalHandle = setInterval(() => {
            if (window.sce_instance !== undefined) {
                clearInterval(intervalHandle);
                chatLoaded = true;
                res();
            }
        }, 50);
    });

    // do no add another watchers if chat is already loaded
    if (chatLoaded) return;

    promise.then(() => {
        // Allow external integrations to setup session watching
        if (sessionWatcherCallback) {
            sessionWatcherCallback(sce_instance);
        }

        sce_instance.onClose(() => {
            storage.removeItem("chat-active");
        });
    });

    return promise;
};

const show = async (
    isProactiveChat = false,
    campaignCode?: string,
    creativeCode?: string,
    playerData?: ChatPlayerData | null
) => {
    if (environment.webView.isAppWebView && environment.appName !== "sazkabet") {
        triggerMobileAppEvent("showChat", campaignCode);
        return;
    }

    Loader.show();

    try {
        await loadChatProvider();
    } catch (e) {
        logException("chat-load-error", e as Error, {
            isProactiveChat,
            campaignCode,
            creativeCode,
        });
        if (!isProactiveChat) {
            showAlert({
                title: "Chat je nedostupný",
                subText:
                    "Omlouváme se, chat je momentálně nedostupný, kontaktujte nás prosím telefonicky na 266 121 212.",
                btnCloseText: "OK",
                btnConfirmText: "Zavolat",
                btnConfirmHref: "tel:266121212",
                btnConfirmType: "primary",
                btnCloseType: "secondary",
            });
        }
        return;
    } finally {
        Loader.hide();
    }

    const flow: string = environment.nodeAliasPath === "/registrace" ? "NativniReg" : "";

    // Use provided player data or defaults
    const defaultPlayerData: ChatPlayerData = {
        Username: "",
        PlayerID: "",
        FirstName: "",
        Language: "CZE",
        Balance: "",
        Currency: "CZK",
        VipLevel: "",
        Depositor: "N",
        SessionKey: undefined,
    };

    const player = {
        Brand: "SZK",
        ...(playerData || defaultPlayerData),
        Gender: "", // we dont have the information
        PlayMode: "",
        BonusPolicyId: "",
        ProactiveChat: isProactiveChat ? "true" : undefined,
        campaignCode: campaignCode || undefined,
        creativeCode: creativeCode || undefined,
        flow,
    };

    const playerClone = structuredClone(player);
    delete playerClone.SessionKey; // We dont want to log session token, coz safety

    logDebugEvent("deh-general", "show-chat", playerClone);
    sce_instance.showChat(player);
    storage.setItem("chat-active", "true", "essential");
    setupCoBrowsingListener();
};

const registerChatToWindow = () => {
    if (!window.showChat) {
        window.showChat = () => void show(false);
    }
};

function setupCoBrowsingListener() {
    window.addEventListener(
        "message",
        (event) => {
            if (event.origin != `https:${appSettings["Integrations:Chat:BaseAddress"]}`) return;
            if (event.data?.type === "COMMAND" && event.data?.payload?.command === "SURFLY_START") {
                fixedHelpCobrowsingMode();
            }
        },
        { passive: false }
    );
}

function callShowChat(arg: string = null) {
    show(false, arg);
}

// TODO: Temporary solution, native chat is not implemented yet
// SEE: https://sazka.atlassian.net/browse/FE-2857
// The binding must be here, because circular dependency
// Only set if not already set by integration layer
if (!window.callShowChat) {
    window.callShowChat = callShowChat;
}

export const useChat = () => {
    storage = useSessionStorage();
    if (storage.getItem("chat-active") || appSettings["Kentico:DisableChatLoadOptimalization"] === "True") {
        loadChatProvider();
    }

    return {
        registerChatToWindow,
        show,
    };
};
